import axios from "axios";
import { jwtDecode } from "jwt-decode"; 
import { signOut } from "../Services/loginService";

const API_BASE_URL = "https://lexington.myhoa.club/api".replace(/\/+$/, '');

// Base API Configuration
const api = axios.create({
  baseURL: API_BASE_URL
  // ❌ Removed default Content-Type to allow FormData for file uploads
});

// INTERCEPTOR to inject token and conditionally set Content-Type
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);

        // 🧠 Check if token is expired
        if (decoded.exp < currentTime) {
          alert("Your session has expired. Please log in again.");
          signOut();
          throw new axios.Cancel("Token expired");
        }

        config.headers["Authorization"] = `Bearer ${token}`;
      } catch (error) {
        console.error("Error decoding token:", error);
        signOut();
        throw new axios.Cancel("Invalid token");
      }
    }

    // ✅ Only set Content-Type for non-FormData requests
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 🔹 RESPONSE INTERCEPTOR — Handle 401 from backend (expired or invalid token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn("Unauthorized — clearing session");
      alert("Your session has expired or is invalid. Please log in again.");
      signOut();
    }
    return Promise.reject(error);
  }
);

// ---------------- Existing API Endpoints ----------------

export const fetchFolders = () => api.get("/folders");
export const fetchFiles = () => api.get("/files");
export const fetchViolations = () => api.get("/getViolations");
export const fetchGeneralRequests = () => api.get("/getGeneralRequests");
export const fetchArchitecturalRequests = () => api.get("/getArchitecturalRequests");
export const fetchAssignableUsers = () => api.get("/users-by-role");

export const uploadFile = (formData) => api.post("/upload", formData); // FormData works now

export const updateNotice = (id, notice) =>
  api.post(`/updateNotice/${id}`, { notice });

export const fetchNotices = () => api.get("/notices"); // Notices

// Utility: File download
export const downloadFile = (filename) =>
  api.get(`/images/${filename}`, { responseType: "blob" });

// Calendar APIs
export const fetchEvents = () => api.get("/getEvents");
export const addEvent = (eventData) => api.post("/addEvent", eventData);
export const editEvent = (id, updatedData) => api.put(`/editEvent/${id}`, updatedData);
export const deleteEvent = (id) => api.delete(`/deleteEvent/${id}`);

// User Management
export const fetchUsers = () => api.get("/users");

// Analytics
export const fetchTopMembers = () => api.get("/top-members");

export { API_BASE_URL };
export default api;
